import { HorizontalSortableTiles } from "../components/game/word";
import '../css/rules.css';

export function RulesComponent() {
    const makeTiles = (word: string, prefix: string) =>
        Array.from(word).map((letter, i) => ({
            letter,
            id: `${prefix}-${i}`,
            faceUp: true
        }));

    return (
        <>
            <div className="rules-container">
                <div>
                    <h1>How to Play</h1>
                    <p className="intro-text">
                        Pirate Scrabble (also known as Anagrams or Snatch) is a tile-based word game where players form and steal words using shared letter tiles. The goal is to end with the longest words for maximum points.
                    </p>
                </div>

                <section className="section-spacing">
                    <h2>Basic Gameplay</h2>
                    
                    <div className="callout-box callout-blue">
                        <p className="callout-title">Each Turn:</p>
                        <ol>
                            <li>Flip one face-down tile face up</li>
                            <li>Any player can buzz in (press <span className="kbd-key">spacebar</span>) to claim or steal a word</li>
                        </ol>
                    </div>

                    <div className="subsection">
                        <p className="subsection-title">Making a new word:</p>
                        <p className="subsection-text">Form any valid word using only face-up tiles.</p>
                    </div>
                    
                    <div className="subsection">
                        <p className="subsection-title">Stealing a word:</p>
                        <p className="subsection-text">Rearrange a claimed word + optionally add any number of face-up tiles to create a new word with a different root.</p>
                    </div>

                    <p className="subsection-text" style={{paddingTop: '0.5rem'}}>
                        <strong>Game ends</strong> when all tiles are flipped and no more words can be formed or stolen. Highest score wins!
                    </p>
                </section>

                <section className="section-spacing">
                    <h2>Stealing Rules</h2>

                    <div className="callout-box callout-amber">
                        <h3 className="callout-title">🔑 The Different Root Rule</h3>
                        <p>
                            You can only steal if your new word has a <strong>different root</strong> from the word you're stealing. Simply adding prefixes, suffixes, or making it plural doesn't count.
                        </p>
                    </div>

                    <div style={{marginTop: '1rem'}}>
                        <h3>Word History Matters</h3>
                        <p className="subsection-text">
                            The root rule applies to a word's <em>entire history</em>, not just its current form. You can view a word's history in-game by dragging it to the staging area.
                        </p>

                        <div className="example-box">
                            <p className="example-label">Example:</p>
                            
                            <div>
                                <HorizontalSortableTiles
                                    id="strain"
                                    items={makeTiles("STRAIN", "strain")}
                                />

                                <div className="steal-indicator-arrow steal-allowed">
                                    <span className="arrow">↓</span>
                                    <span> Steal allowed</span>
                                </div>

                                <HorizontalSortableTiles
                                    id="trains"
                                    items={makeTiles("TRAINS", "trains")}
                                />

                                <div className="steal-indicator-arrow steal-not-allowed">
                                    <span className="arrow">↓</span>
                                    <span> Steal NOT allowed</span>
                                </div>

                                <HorizontalSortableTiles
                                    id="strains"
                                    items={makeTiles("STRAINS", "strains")}
                                />
                            </div>

                            <div className="explanation-box">
                                <p>STRAIN → TRAINS is legal (different roots). But TRAINS → STRAINS is illegal because STRAINS shares a root with STRAIN, which appears in the word's history.</p>
                            </div>
                        </div>
                    </div>

                    <div className="faq-box">
                        <h3 className="faq-question">Can I steal my own words?</h3>
                        <p className="faq-answer">Yes, you can steal from yourself as long as you follow all the standard rules.</p>
                    </div>
                </section>

                <section className="section-spacing">
                    <h2>Scoring</h2>

                    <div className="callout-box callout-purple">
                        <p className="callout-title">Longer words = more points</p>
                        <p>
                            <code className="code-inline">Points = Word Length − Minimum Word Length + 1</code>
                        </p>
                    </div>

                    <div className="score-example">
                        <p className="example-label">Example (Minimum word length: 4)</p>
                        
                        <div className="player-section">
                            <p className="player-name-alice">Alice:</p>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="blade" items={makeTiles("BLADE", "blade")} />
                                <span className="points-calculation">(5 − 4 + 1 = 2 pts)</span>
                            </div>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="train" items={makeTiles("TRAIN", "train")} />
                                <span className="points-calculation">(5 − 4 + 1 = 2 pts)</span>
                            </div>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="book" items={makeTiles("BOOK", "book")} />
                                <span className="points-calculation">(4 − 4 + 1 = 1 pt)</span>
                            </div>
                            <p className="total-score">Alice's total: 5 points</p>
                        </div>

                        <div className="player-section">
                            <p className="player-name-bob">Bob:</p>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="slide" items={makeTiles("SLIDE", "slide")} />
                                <span className="points-calculation">(5 − 4 + 1 = 2 pts)</span>
                            </div>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="congregate" items={makeTiles("CONGREGATE", "congregate")} />
                                <span className="points-calculation">(10 − 4 + 1 = 7 pts)</span>
                            </div>
                            <div className="word-with-points">
                                <HorizontalSortableTiles id="mist" items={makeTiles("MIST", "mist")} />
                                <span className="points-calculation">(4 − 4 + 1 = 1 pt)</span>
                            </div>
                            <p className="total-score">Bob's total: 11 points</p>
                        </div>
                    </div>

    
                </section>

                <section className="section-spacing">
                    <h2>About Puzzles</h2>
                    <p className="subsection-text">
                        Puzzle mode simulates challenging Pirate Scrabble scenarios with long steal chains. Solutions use roughly the 60,000 most common English words, though more obscure words can still be submitted.
                    </p>
                    <p className="text-small" style={{marginTop: '0.5rem'}}>
                        The scoring dictionary matches the official Scrabble dictionary.
                    </p>
                </section>
            </div>
        </>
    );
}
